import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { ConfirmationDialog, NumberStepper, Section, SegmentedControl, Toggle } from "@hicya/ui";

import { AppearanceChoices } from "@/components/settings/appearance-choices";
import {
  SETTINGS_SECTIONS,
  getSettingsSection,
  settingsHref,
} from "@/components/settings/settings-navigation";
import { HabitIcon } from "@/components/ui/habit-icon";
import { ColorPicker } from "@/components/form/color-picker";
import { IconPicker } from "@/components/form/icon-picker";
import { HabitsAccountSection } from "@/components/settings/account-section";
import { useHabits } from "@/context/habits-context";
import { useDatabase } from "@/db/provider";
import { useSync } from "@/sync/provider";
import type { ThemeName } from "@/theme/constants";
import { useTheme } from "@/theme/useTheme";
import { downloadFile } from "@/utils/download";

import styles from "./settings.module.scss";

const FIRST_DAY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "0", label: "Sunday" },
  { value: "1", label: "Monday" },
];

function formatRelativeTime(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function Settings() {
  const location = useLocation();
  const navigate = useNavigate();
  const section = getSettingsSection(location.search);
  const panelTitle = useRef<HTMLHeadingElement>(null);
  const previousSection = useRef(section);
  useEffect(() => {
    if (previousSection.current !== section) panelTitle.current?.focus();
    previousSection.current = section;
  }, [section]);
  const {
    state,
    updateSettings,
    createCategory,
    updateCategory: updateCategoryFn,
    deleteCategory,
    refresh,
  } = useHabits();
  const { settings, categories } = state;
  const { theme, setTheme } = useTheme();
  const db = useDatabase();
  const { syncState, lastSyncAt, error: syncError, triggerSync } = useSync();
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const [editIcon, setEditIcon] = useState("");
  const [showDeleteCategoryConfirm, setShowDeleteCategoryConfirm] = useState<string | null>(null);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryColor, setNewCategoryColor] = useState("#86b5c4");
  const [newCategoryIcon, setNewCategoryIcon] = useState("\u2764\uFE0F");
  const [showAddCategory, setShowAddCategory] = useState(false);

  const categoryHabitCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const habit of state.habits) {
      if (habit.category_id) {
        counts.set(habit.category_id, (counts.get(habit.category_id) ?? 0) + 1);
      }
    }
    return counts;
  }, [state.habits]);

  const handleStartEdit = useCallback(
    (cat: { id: string; name: string; color: string; icon: string }) => {
      setEditingCategoryId(cat.id);
      setEditName(cat.name);
      setEditColor(cat.color);
      setEditIcon(cat.icon);
    },
    [],
  );

  const handleSaveEdit = useCallback(async () => {
    if (!editingCategoryId || !editName.trim()) return;
    await updateCategoryFn(editingCategoryId, {
      name: editName.trim(),
      color: editColor,
      icon: editIcon,
    });
    setEditingCategoryId(null);
  }, [editingCategoryId, editName, editColor, editIcon, updateCategoryFn]);

  const handleDeleteCategory = useCallback(
    async (id: string) => {
      await deleteCategory(id);
      setShowDeleteCategoryConfirm(null);
    },
    [deleteCategory],
  );

  const handleAddCategory = useCallback(async () => {
    if (!newCategoryName.trim()) return;
    await createCategory({
      name: newCategoryName.trim(),
      color: newCategoryColor,
      icon: newCategoryIcon,
    });
    setNewCategoryName("");
    setNewCategoryColor("#86b5c4");
    setNewCategoryIcon("\u2764\uFE0F");
    setShowAddCategory(false);
  }, [newCategoryName, newCategoryColor, newCategoryIcon, createCategory]);

  const handleThemeChange = useCallback(
    (theme: ThemeName) => {
      setTheme(theme);
      void updateSettings("theme", theme);
    },
    [setTheme, updateSettings],
  );

  const handleDensityChange = useCallback(
    (density: string) => {
      void updateSettings("density", density);
    },
    [updateSettings],
  );

  const handleFirstDayChange = useCallback(
    (value: string) => {
      void updateSettings("firstDayOfWeek", value);
    },
    [updateSettings],
  );

  const handleBoundaryChange = useCallback(
    (hour: number) => {
      void updateSettings("dayBoundaryHour", String(hour));
    },
    [updateSettings],
  );

  const handleGraceDaysChange = useCallback(
    (days: number) => {
      void updateSettings("graceDaysPerWeek", String(days));
    },
    [updateSettings],
  );

  const handleStreaksChange = useCallback(
    (checked: boolean) => {
      void updateSettings("showStreaks", String(checked));
    },
    [updateSettings],
  );

  const handleExportJSON = useCallback(async () => {
    const json = await db.exportAllAsJSON();
    const date = new Date().toISOString().slice(0, 10);
    downloadFile(json, `habits-export-${date}.json`, "application/json");
  }, [db]);

  const handleExportCSV = useCallback(async () => {
    const csv = await db.exportAsCSV();
    const date = new Date().toISOString().slice(0, 10);
    downloadFile(csv, `habits-export-${date}.csv`, "text/csv");
  }, [db]);

  const handleClearData = useCallback(async () => {
    await db.clearAllData();
    setTheme("system");
    setShowClearConfirm(false);
    await refresh({ includeSettings: true });
  }, [db, setTheme, refresh]);

  return (
    <div className={styles.page}>
      <h2 className={styles.heading}>Settings</h2>

      <div className={styles.sections}>
        <div className={styles.account}>
          <HabitsAccountSection />
        </div>

        {syncState !== "idle" || lastSyncAt ? (
          <Section title="Sync">
            <div className={styles.syncInfo}>
              <div className={styles.syncRow}>
                <span className={styles.label}>Status</span>
                <span className={styles.syncStatus}>
                  {syncState === "syncing"
                    ? "Syncing..."
                    : syncState === "error"
                      ? "Error"
                      : "Up to date"}
                </span>
              </div>
              {lastSyncAt && (
                <div className={styles.syncRow}>
                  <span className={styles.label}>Last synced</span>
                  <span className={styles.syncTime}>{formatRelativeTime(lastSyncAt)}</span>
                </div>
              )}
              {syncError && <p className={styles.syncError}>{syncError}</p>}
              <button
                type="button"
                className={styles.exportButton}
                onClick={triggerSync}
                disabled={syncState === "syncing"}
                data-testid="sync-now-button"
              >
                {syncState === "syncing" ? "Syncing..." : "Sync now"}
              </button>
            </div>
          </Section>
        ) : null}

        <div className={styles.sectionSelector} data-settings-section-selector>
          <label htmlFor="settings-section">Section</label>
          <select
            id="settings-section"
            value={section}
            onChange={(event) => navigate(settingsHref(event.target.value))}
          >
            {SETTINGS_SECTIONS.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <h3 className={styles.panelTitle} ref={panelTitle} tabIndex={-1}>
          {SETTINGS_SECTIONS.find((item) => item.id === section)?.label}
        </h3>
        <div hidden={section !== "appearance"}>
          <AppearanceChoices
            theme={theme}
            density={settings.density}
            onThemeChange={handleThemeChange}
            onDensityChange={handleDensityChange}
          />
        </div>

        <div
          className={styles.panel}
          data-settings-panel="categories"
          hidden={section !== "categories"}
        >
          <div className={styles.categoryList}>
            {categories.map((cat) =>
              editingCategoryId === cat.id ? (
                <div key={cat.id} className={styles.categoryEditForm}>
                  <input
                    className={styles.categoryInput}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Category name"
                    aria-label="Category name"
                    autoFocus
                  />
                  <IconPicker value={editIcon} onChange={setEditIcon} />
                  <ColorPicker value={editColor} onChange={setEditColor} />
                  <div className={styles.categoryEditActions}>
                    <button
                      type="button"
                      className={styles.categoryCancelButton}
                      onClick={() => setEditingCategoryId(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={styles.categorySaveButton}
                      disabled={!editName.trim()}
                      onClick={() => void handleSaveEdit()}
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <div key={cat.id} className={styles.categoryRow}>
                  <span className={styles.categoryIcon}>
                    <HabitIcon value={cat.icon} size={40} />
                  </span>
                  <span className={styles.categoryName}>{cat.name}</span>
                  <span
                    className={styles.categoryColorDot}
                    style={{ backgroundColor: cat.color }}
                  />
                  {(categoryHabitCounts.get(cat.id) ?? 0) > 0 && (
                    <span className={styles.categoryCount}>{categoryHabitCounts.get(cat.id)}</span>
                  )}
                  <div className={styles.categoryActions}>
                    <button
                      type="button"
                      className={styles.categoryActionButton}
                      aria-label={`Edit ${cat.name}`}
                      onClick={() => handleStartEdit(cat)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={styles.categoryActionButton}
                      aria-label={`Delete ${cat.name}`}
                      onClick={() => {
                        const count = categoryHabitCounts.get(cat.id) ?? 0;
                        if (count > 0) {
                          setShowDeleteCategoryConfirm(cat.id);
                        } else {
                          void handleDeleteCategory(cat.id);
                        }
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ),
            )}
          </div>

          {showAddCategory ? (
            <div className={styles.categoryEditForm}>
              <input
                className={styles.categoryInput}
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Category name"
                aria-label="Category name"
                autoFocus
              />
              <IconPicker value={newCategoryIcon} onChange={setNewCategoryIcon} />
              <ColorPicker value={newCategoryColor} onChange={setNewCategoryColor} />
              <div className={styles.categoryEditActions}>
                <button
                  type="button"
                  className={styles.categoryCancelButton}
                  onClick={() => {
                    setShowAddCategory(false);
                    setNewCategoryName("");
                    setNewCategoryColor("#86b5c4");
                    setNewCategoryIcon("\u2764\uFE0F");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={styles.categorySaveButton}
                  disabled={!newCategoryName.trim()}
                  onClick={() => void handleAddCategory()}
                >
                  Add
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className={styles.exportButton}
              onClick={() => setShowAddCategory(true)}
            >
              Add Category
            </button>
          )}
        </div>

        <div
          className={styles.panel}
          data-settings-panel="tracking"
          hidden={section !== "tracking"}
        >
          <div className={styles.field}>
            <span className={styles.label}>First day of week</span>
            <SegmentedControl
              options={FIRST_DAY_OPTIONS}
              value={String(settings.firstDayOfWeek)}
              onChange={handleFirstDayChange}
              data-testid="first-day-selector"
              aria-label="First day of week"
            />
          </div>
          <NumberStepper
            value={settings.dayBoundaryHour}
            onChange={handleBoundaryChange}
            min={0}
            max={6}
            label="Day boundary hour"
            data-testid="boundary-hour-stepper"
          />
          <NumberStepper
            value={settings.graceDaysPerWeek}
            onChange={handleGraceDaysChange}
            min={0}
            max={3}
            label="Grace days per week"
            data-testid="grace-days-stepper"
          />
          <div className={styles.toggleField}>
            <span className={styles.label}>Show streaks by default</span>
            <Toggle
              checked={settings.showStreaks}
              onChange={handleStreaksChange}
              data-testid="streaks-toggle"
              aria-label="Show streaks by default"
            />
          </div>
        </div>

        <div className={styles.panel} data-settings-panel="data" hidden={section !== "data"}>
          <div className={styles.exportButtons}>
            <button
              type="button"
              className={styles.exportButton}
              onClick={() => void handleExportJSON()}
              data-testid="export-json-button"
            >
              Export as JSON
            </button>
            <button
              type="button"
              className={styles.exportButton}
              onClick={() => void handleExportCSV()}
              data-testid="export-csv-button"
            >
              Export as CSV
            </button>
          </div>
          <button
            type="button"
            className={styles.dangerButton}
            onClick={() => setShowClearConfirm(true)}
            data-testid="clear-data-button"
          >
            Clear All Data
          </button>
        </div>
      </div>

      {showClearConfirm && (
        <ConfirmationDialog
          title="Clear All Data"
          message="This will delete all habits, completions, and settings. This cannot be undone."
          confirmLabel="Clear Everything"
          destructive
          onConfirm={() => void handleClearData()}
          onCancel={() => setShowClearConfirm(false)}
        />
      )}

      {showDeleteCategoryConfirm && (
        <ConfirmationDialog
          title="Delete Category"
          message={`This category has ${categoryHabitCounts.get(showDeleteCategoryConfirm) ?? 0} habit(s) assigned. They will become uncategorized.`}
          confirmLabel="Delete"
          destructive
          onConfirm={() => void handleDeleteCategory(showDeleteCategoryConfirm)}
          onCancel={() => setShowDeleteCategoryConfirm(null)}
        />
      )}
    </div>
  );
}
